import railsPostFetch from '@aytm/lib/common/rails-post-fetch'
import { delay } from '@aytm/lib/common/promise'
import mapMessages from '../../common/mapMessages'
import { generateUID } from '@aytm/lib/common/uid'
import { ChatMessage } from '../../types'
import ChatError from '../../common/ChatError'
import { API_COMMAND_ENDPOINT, API_GLOBAL_COMMAND_ENDPOINT } from './const'
import { SKIPPER_AUTHOR_ID } from '../../const'
import { CommandParams, AssistantCommandResponse, SharedAssistantState } from './types'

const generalCommandErrorMessage: ChatMessage = {
  error: true,
  id: generateUID(),
  timestamp: Date.now(),
  text: 'Arr, the message bottle did not reach its destination.',
  author: SKIPPER_AUTHOR_ID
}
export const authErrorMessage = {
  error: true,
  id: generateUID(),
  timestamp: Date.now(),
  text: 'Oops — your chat session took a little nap! [Refresh](#reload_page) the page to wake it up.',
  author: SKIPPER_AUTHOR_ID
}

export default async function sendCommand<T extends SharedAssistantState>(
  command: string,
  payload: object,
  { assistantState, update, awaitContext, debugName = 'ChatAssistant' }: CommandParams<T>,
  tryCount = 10
): Promise<ChatError | undefined> {
  let remainingTries = tryCount
  assistantState.inputReady = false
  // filter out error messages
  assistantState.messages = assistantState.messages.filter(message => !message.error)
  update()

  try {
    const commandContext = await awaitContext({ assistantState }).catch(error => {
      throw error instanceof ChatError ? error : new ChatError(String(error))
    })

    const commandParams = {
      domain: assistantState.domain,
      command,
      payload,
      ids: assistantState.messages.map(m => m.id),
      context: commandContext
    }

    const API_ENDPOINT = null != assistantState.chatID ? API_COMMAND_ENDPOINT : API_GLOBAL_COMMAND_ENDPOINT

    const commandResponse = await railsPostFetch(API_ENDPOINT.URL.replace('{chatID}', String(assistantState.chatID)), {
      method: API_ENDPOINT.method,
      body: JSON.stringify(commandParams)
    })

    if (401 === commandResponse.status) {
      remainingTries = 0 // do not retry on auth error
      throw new ChatError('Unauthorized')
    }
    if (200 !== commandResponse.status) {
      throw new ChatError('Failed to send command')
    }

    const response = (await commandResponse.json()) as AssistantCommandResponse
    if (Array.isArray(response.messages)) {
      assistantState.messages = mapMessages(response.messages, assistantState.messages)
    }

    if (null != response.inputReady) {
      assistantState.inputReady = response.inputReady
    }

    update()

    if ('development' === process.env.NODE_ENV) {
      // eslint-disable-next-line no-console
      console.debug(`${debugName}: command done`)
    }
  } catch (error) {
    if ('development' === process.env.NODE_ENV) {
      // eslint-disable-next-line no-console
      console.error(`${debugName}: command error`, (error as ChatError)?.message, (error as ChatError)?.payload)
    }

    const authError = 'Unauthorized' === (error as ChatError)?.message

    assistantState.messages = [
      ...assistantState.messages,
      authError
        ? authErrorMessage
        : tryCount > 0
          ? { ...generalCommandErrorMessage, text: generalCommandErrorMessage.text + `\n\nWill try again shortly...` }
          : generalCommandErrorMessage
    ]
    assistantState.inputReady = false
    update()

    // repeat send command
    if (remainingTries > 0) {
      await delay(3000)
      const resolution = await sendCommand(
        command,
        payload,
        { assistantState, update, awaitContext, debugName },
        remainingTries - 1
      )
      return resolution
    } else {
      return error instanceof ChatError
        ? error
        : new ChatError(error instanceof Error ? error.message : 'Unknown error')
    }
  }

  return undefined
}
